import type {
	IrrigationPhasePayload,
	IrrigationSystemPayload,
	ModuleFull,
} from "./irrigationTypes";
import type { FormikHelpers } from "formik";
import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { initialValues, validationSchema, getSchemas } from "./irrigation";
import {
	useAppSelector,
	useThreadsProcessor,
} from "@app/hooks";
import {
	useUpdateIrrigationSystemMutation,
	useUpdateIrrigationPhaseMutation,
	useContextAwareGetIrrigationPhase,
	useContextAwareGetIrrigationSystem,
	useContextAwareGetIrrigation,
	useUpdateIrrigationsMutation,
	useContextAwareGetLandUseChange,
} from "@hooks/index";
import { selectCurrentProject } from "@features/project/projectSlice";
import useModuleHook from "../useModuleHook";
import useErrorMessage from "@utils/useErrorMessage";
import IrrigationTierOne from "./IrrigationTierOne";
import { BEModulesClassNames, FEModules } from "@utils/moduleList";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import GenericTierTwo from "../GenericTierTwo";

const Irrigation = () => {
	const { activeActivityId, activity } = useAppSelector(selectCurrentProject);
	const { moduleList } = useAppSelector(selectCurrentBuilder);
	const id = moduleList.find(m => m.id === FEModules.Irrigation)?.uniqueId;
	const lucId = moduleList.find((module) => module.id === FEModules.LandUseChange)?.uniqueId;

	const {
		data: irrigationsData,
		isLoading,
		isError,
		error,
	} = useContextAwareGetIrrigation(id ?? 0, { refetchOnMountOrArgChange: true, });
	const { data: lucData } = useContextAwareGetLandUseChange(lucId ?? 0, {
		refetchOnMountOrArgChange: true,
		skip: !lucId
	});
	const [updateIrrigations,
		{
			error: updateIrrigationsError,
			isError: isUpdateIrrigationsErros,
			isLoading: isLoadingIrrigations,
		},
	] = useUpdateIrrigationsMutation();

	const {
		data: irrigationSysData,
		isLoading: isSysLoading,
		isError: isSysError,
		error: sysError,
	} = useContextAwareGetIrrigationSystem({ activityId: activeActivityId ?? 0 }, {
		refetchOnMountOrArgChange: true,
	});
	const {
		data: operationPhasesData,
		isLoading: isOpLoading,
		isError: isOpError,
		error: opError,
	} = useContextAwareGetIrrigationPhase({ activityId: activeActivityId ?? 0 }, {
		refetchOnMountOrArgChange: true,
	});

	const [
		updateIrrigationSystem,
		{
			isLoading: isLoadingUpdateSystem,
			isError: isUpdateSystemError,
			error: updateSystemError,
		},
	] = useUpdateIrrigationSystemMutation();
	const [
		updateIrrigationPhase,
		{
			isLoading: isLoadingUpdatePhase,
			isError: isUpdatePhaseError,
			error: updatePhaseError,
		},
	] = useUpdateIrrigationPhaseMutation();

	const { moduleSchema, t2Schema } = useMemo(() => getSchemas(), []);
	const [initValues, setInitValues] = useState<ModuleFull>(initialValues);

	const { errorMsg } = useModuleHook({
		skeletonsLoadingDeps: [isLoading, isLoadingIrrigations, isSysLoading, isOpLoading, isLoadingUpdateSystem, isLoadingUpdatePhase],
		isMutateError: isUpdateIrrigationsErros || isUpdateSystemError || isUpdatePhaseError,
		mutateError: updateIrrigationsError ?? updateSystemError ?? updatePhaseError,
	});
	const { errorMsg: blockingError } = useErrorMessage({ isError, error });
	const { errorMsg: blockingSystemError } = useErrorMessage({ isError: isSysError, error: sysError, });
	const { errorMsg: blockingPhaseError } = useErrorMessage({ isError: isOpError, error: opError, });
	const { processModuleThreads } = useThreadsProcessor<ModuleFull>();

	useEffect(() => {
		if (!irrigationSysData || !operationPhasesData) return;
		processModuleThreads({ irrigations: irrigationSysData, irrigationPhases: operationPhasesData, })
	}, [irrigationSysData, operationPhasesData, processModuleThreads]);

	useEffect(() => {
		if (irrigationsData) setInitValues((cur) => ({ ...cur, module: { ...irrigationsData, area: lucData?.module.area ?? null } }));
	}, [irrigationsData, lucData, activity, moduleList]);

	const handleSubmit = async (
		values: ModuleFull,
		{ resetForm }: FormikHelpers<ModuleFull>,
	) => {

		try {
			const promises = [];

			//update systems
			for (const dist of values.irrigations) {
				const irrigationPayload: IrrigationSystemPayload = {
					body: dist,
					activityId: activeActivityId ?? 0,
				};

				if (dist.id) promises.push(updateIrrigationSystem(irrigationPayload))
			}

			//update phases
			for (const dist of values.irrigationPhases) {
				const phasePayload: IrrigationPhasePayload = {
					body: dist,
					activityId: activeActivityId ?? 0,
				};

				if (dist.id) promises.push(updateIrrigationPhase(phasePayload));
			}

			await Promise.all(promises);
			await new Promise(resolve => setTimeout(resolve, 500));
			if (values.module.id) await updateIrrigations({ data: values, activityId: activeActivityId ?? 0 });

			resetForm({ values });
		} catch (error) {
			console.error("irrigation error", error);
		}
	};

	return (
		<Formik
			initialValues={initValues}
			onSubmit={handleSubmit}
			validationSchema={validationSchema}
			enableReinitialize
		>
			<>
				<IrrigationTierOne
					title="project.irrigation"
					initValues={initValues}
					t2Schema={t2Schema}
					beModuleType={BEModulesClassNames.Irrigation}
					moduleSchema={moduleSchema}
					footerError={errorMsg}
					blockingError={blockingError ?? blockingSystemError ?? blockingPhaseError}
					isError={isUpdateSystemError || isUpdatePhaseError}
					setInitValues={setInitValues}
				/>
				<GenericTierTwo
					title="project.irrigation"
					t2Schema={t2Schema}
				/>
			</>
		</Formik>
	);
};

export default Irrigation;
